/**
 * Dates for the Job bar and Job history, on the Sydney calendar with the
 * office's month names. Spelled out here, never taken from the locale: en-AU
 * spells September "Sept", and the office reads "Sep" (QuoteRevisionsSheet
 * uses the same table).
 */

const TIME_ZONE = 'Australia/Sydney';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

let partsFormat: Intl.DateTimeFormat | null = null;

interface SydneyParts { day: number; month: string; year: string; hour: number; minute: number }

function sydneyParts(value: unknown): SydneyParts | null {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) return null;
    partsFormat ??= new Intl.DateTimeFormat('en-AU', {
        day: 'numeric', month: 'numeric', year: 'numeric',
        hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
        timeZone: TIME_ZONE,
    });
    const parts: Record<string, string> = {};
    partsFormat.formatToParts(date).forEach((part) => { parts[part.type] = part.value; });
    const month = MONTHS[Number(parts.month) - 1];
    const day = Number(parts.day);
    const hour = Number(parts.hour) % 24;
    const minute = Number(parts.minute);
    if (!month || !Number.isFinite(day) || !parts.year || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return { day, month, year: parts.year, hour, minute };
}

/** "12 Sep", or null when the value is not a date. */
export function formatDayMonth(value: unknown): string | null {
    const p = sydneyParts(value);
    return p ? `${p.day} ${p.month}` : null;
}

/** "12 Sep 2026", or null. */
export function formatFullDate(value: unknown): string | null {
    const p = sydneyParts(value);
    return p ? `${p.day} ${p.month} ${p.year}` : null;
}

function clock(p: SydneyParts): string {
    const hour12 = p.hour % 12 || 12;
    return `${hour12}:${String(p.minute).padStart(2, '0')} ${p.hour < 12 ? 'am' : 'pm'}`;
}

/** The History "When" cell: "14 Sep 9:40 am", with the year in the title. */
export function formatWhen(value: unknown): { line: string; title: string } {
    const p = sydneyParts(value);
    if (!p) return { line: 'Date not recorded', title: 'Date not recorded' };
    return { line: `${p.day} ${p.month} ${clock(p)}`, title: `${p.day} ${p.month} ${p.year} ${clock(p)}` };
}
